import React, { useState } from 'react';
import { ExternalLink } from 'lucide-react';
import { Building } from '../types/protocols';
import { BaseComponentImpl } from '../types/implementations';
import { translate } from '../types/EnumTranslations';
import { getBuildComponentTypeTranslation } from '../Helpers/BuildComponentTypesStore';
import { getComponentIconPaths } from '../Helpers/componentTypeIconUtils';
import { componentLocationLabel } from '../Helpers/componentLocationLabel';
import LocationDisplay, { useComponentLocations } from './LocationDisplay';
import './LinkedComponentCard.css';

/**
 * The card for a component linked to a work order or an issue (felanmälan):
 * type icon, name link, type line, location line and the component's
 * positions as click-to-enlarge thumbnails. Condition, photos and history
 * stay on the component page behind the link.
 */

/**
 * A component's blueprint/satellite/photo positions as click-to-open thumbnails,
 * stacked horizontally. Renders nothing when the component has no positions.
 * The shared LocationDisplay handles the full-screen image overlay on click.
 */
export const ComponentLocationThumbnails: React.FC<{
    component: BaseComponentImpl;
    building: Building;
}> = ({ component, building }) => {
    const locations = useComponentLocations(component, building);
    if (locations.length === 0) return null;
    return <LocationDisplay locations={locations} />;
};

/**
 * The component-type icon shown before the component title, using the same
 * resolution as blueprint markers (getComponentIconPaths): specific type icon →
 * BaseType icon → placeholder, cascading through fallbacks on load error.
 */
export const ComponentRowIcon: React.FC<{ component: BaseComponentImpl }> = ({ component }) => {
    const { primary, fallbacks } = getComponentIconPaths(component.specificType?.value ?? undefined, component.type);
    const [fallbackLevel, setFallbackLevel] = useState(0);
    const src = fallbackLevel === 0 ? primary : fallbacks[fallbackLevel - 1];
    return (
        <img
            src={src}
            alt=""
            width={20}
            height={20}
            className="flex-shrink-0"
            onError={() => setFallbackLevel(level => (level < fallbacks.length ? level + 1 : level))}
        />
    );
};

/**
 * Compact type + location lines for a linked component. Always shows the
 * component's base type and specific type (e.g. "Fastighetsteknik · Ventilation"),
 * then "Våning • Rum" when the component belongs to a room/level, else where
 * it sits ("Plats: Fasad norr", "Plats: Tak", "Plats: Utomhus") — see
 * componentLocationLabel.
 */
export const ComponentLocationText: React.FC<{
    component: BaseComponentImpl;
    building: Building;
}> = ({ component, building }) => {
    const { level, room, place } = componentLocationLabel(component, building);

    const baseTypeLabel = component.type ? translate(component.type) : null;
    const specificKey = component.specificType?.value;
    const specificTypeLabel = specificKey
        ? getBuildComponentTypeTranslation(specificKey, component.type)
        : null;
    const typeLabel = [baseTypeLabel, specificTypeLabel]
        .filter((part, i, arr) => part && arr.indexOf(part) === i)
        .join(' · ');

    return (
        <>
            {typeLabel && <div className="small text-muted mt-1">{typeLabel}</div>}
            {(level || room) && (
                <div className="small text-muted mt-1">
                    {level && <span><strong>Våning:</strong> {level}</span>}
                    {level && room && <span className="mx-2">•</span>}
                    {room && <span><strong>Rum:</strong> {room}</span>}
                </div>
            )}
            {!level && !room && place && (
                <div className="small text-muted mt-1"><strong>Plats:</strong> {place}</div>
            )}
        </>
    );
};

export interface LinkedComponentCardProps {
    /** The linked component; undefined while the building is loading or when it is gone. */
    component?: BaseComponentImpl;
    /** The building with `structure` and map images; without it only the name shows. */
    building?: Building | null;
    /** Shown when the component or building is not available. */
    fallbackName?: string;
    /** Link to the component page; null renders the name as plain text (building missing). */
    href: string | null;
    /** Controls pinned top-right next to the thumbnails, e.g. a remove button. */
    actions?: React.ReactNode;
    /** Extra classes on the card, e.g. margins. */
    className?: string;
}

const LinkedComponentCard: React.FC<LinkedComponentCardProps> = ({
    component,
    building,
    fallbackName,
    href,
    actions,
    className,
}) => {
    const name = component && building ? component.displayName(building) : fallbackName || 'Komponent';
    const hasPositions = !!(component && building);
    return (
        <div className={`linked-component-card bg-light rounded p-3 position-relative ${className ?? ''}`.trim()}>
            {/* Thumbnails + actions are pinned to the top-right and taken out of
                the normal flow (position-absolute) so tall thumbnails never grow
                the row or push the title/type/location text below them down. */}
            {(hasPositions || actions) && (
                <div className="position-absolute top-50 end-0 translate-middle-y me-3 d-flex align-items-center gap-2 linked-component-card__thumbnails">
                    {hasPositions && <ComponentLocationThumbnails component={component} building={building} />}
                    {actions}
                </div>
            )}
            {/* Title + type/location text. pe-5 keeps the content clear of the pinned thumbnails. */}
            <div className="pe-5">
                <div className="d-flex align-items-center gap-2">
                    {component && <ComponentRowIcon component={component} />}
                    {href === null ? (
                        <span className="fw-medium text-muted">{name}</span>
                    ) : (
                        <a href={href} target="_blank" rel="noopener noreferrer" className="fw-medium">
                            {name} <ExternalLink size={12} />
                        </a>
                    )}
                </div>
                {component && building && <ComponentLocationText component={component} building={building} />}
            </div>
        </div>
    );
};

export default LinkedComponentCard;
